// v6 — language toggle in the App header.
//
// Two buttons: "Plain English" / "Technical". Either is a real button
// (not a select) so keyboard users can switch with Tab + Enter. The
// active button is highlighted with the accent colour and `aria-pressed`
// is set so screen readers announce the selected state.

import { useLanguage, type Lang } from '../lib/language';

const LABELS: Record<Lang, { full: string; short: string }> = {
  plain: { full: 'Plain English', short: 'Plain' },
  technical: { full: 'Technical', short: 'Technical' },
};

const HINTS: Record<Lang, string> = {
  plain: 'Switch to technical labels and tooltips.',
  technical: 'Switch to plain-language labels and tooltips.',
};

export function LanguageToggle() {
  const { lang, setLang } = useLanguage();

  return (
    <div
      role="group"
      aria-label="Language style"
      className="inline-flex h-11 items-center overflow-hidden rounded-md border border-neutral-300 dark:border-neutral-700"
    >
      <span
        aria-hidden="true"
        className="border-r border-neutral-300 px-2 text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:border-neutral-700 dark:text-neutral-400"
      >
        Lang
      </span>
      {(['plain', 'technical'] as const).map((l) => {
        const active = lang === l;
        const meta = LABELS[l];
        return (
          <button
            key={l}
            type="button"
            aria-pressed={active}
            aria-label={`${meta.full}. ${HINTS[l]}`}
            title={HINTS[l]}
            onClick={() => setLang(l)}
            className={[
              'inline-flex h-11 cursor-pointer items-center justify-center px-3 text-xs font-medium motion-safe:transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              active
                ? 'bg-accent text-white'
                : 'bg-transparent text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800',
            ].join(' ')}
          >
            {meta.full}
          </button>
        );
      })}
    </div>
  );
}
